import { ErrorResponse, ListPayload } from 'core/http/response';
import { Reducer } from 'redux';
import { Invoice } from './types';
import { InvoiceActions, InvoiceActionTypes } from './actionTypes';

export interface InvoicesState {
    isLoading: boolean;
    isSaving: boolean;
    error?: ErrorResponse;
    invoicesListResponse: ListPayload<Invoice>;
    entities: Record<string, Invoice>;
    currentInvoiceId?: string;
}

export const invoicesInitialState: Readonly<InvoicesState> = {
    isLoading: false,
    isSaving: false,
    error: undefined,
    invoicesListResponse: {
        list: [],
        pageNumber: 0,
        pageSize: 10,
        total: 0,
    },
    entities: {},
    currentInvoiceId: undefined,
};

const invoicesReducer: Reducer<InvoicesState, InvoiceActionTypes> = (
    state = invoicesInitialState,
    action
): InvoicesState => {
    switch (action.type) {
        case InvoiceActions.GET_INVOICE_BY_ID_SUCCESS: {
            const invoice = action.payload;
            return {
                ...state,
                isLoading: false,
                error: undefined,
                currentInvoiceId: invoice.id,
                entities: {
                    ...state.entities,
                    [invoice.id]: invoice,
                },
            };
        }

        case InvoiceActions.UPDATE_INVOICE_SUCCESS: {
            const invoice = action.payload;
            return {
                ...state,
                isLoading: false,
                error: undefined,
                entities: {
                    ...state.entities,
                    [invoice.id]: invoice,
                },
            };
        }

        case InvoiceActions.LIST_INVOICES_SUCCESS: {
            const { list, pageNumber, pageSize, total } = action.payload;

            // Convert list to entities
            const newEntities = list.reduce<Record<string, Invoice>>((acc, invoice) => {
                acc[invoice.id] = invoice;
                return acc;
            }, {});

            return {
                ...state,
                isLoading: false,
                error: undefined,
                invoicesListResponse: {
                    list,
                    pageNumber,
                    pageSize,
                    total,
                },
                entities: {
                    ...state.entities,
                    ...newEntities,
                },
            };
        }

        case InvoiceActions.ARCHIVE_INVOICE_SUCCESS: {
            const invoice = action.payload;
            const existing = state.entities[invoice.id]
                ? {
                      ...state.entities[invoice.id],
                      archived: true,
                  }
                : undefined;

            if (!existing) return { ...state, isLoading: false, error: undefined };

            return {
                ...state,
                isLoading: false,
                error: undefined,
                entities: { ...state.entities, [invoice.id]: existing },
            };
        }

        case InvoiceActions.UNARCHIVE_INVOICE_SUCCESS: {
            const invoice = action.payload;
            const existing = state.entities[invoice.id]
                ? {
                      ...state.entities[invoice.id],
                      archived: false,
                  }
                : undefined;

            if (!existing) return { ...state, isLoading: false, error: undefined };

            return {
                ...state,
                isLoading: false,
                error: undefined,
                entities: { ...state.entities, [invoice.id]: existing },
            };
        }

        case InvoiceActions.DUPLICATE_INVOICE_SUCCESS: {
            const newInvoice = action.payload;
            const shouldAddToList = true;

            const updatedList = shouldAddToList
                ? [newInvoice, ...state.invoicesListResponse.list].slice(0, state.invoicesListResponse.pageSize)
                : state.invoicesListResponse.list;
            const updatedTotal = state.invoicesListResponse.total + 1;

            return {
                ...state,
                isLoading: false,
                error: undefined,
                invoicesListResponse: {
                    ...state.invoicesListResponse,
                    list: updatedList,
                    total: updatedTotal,
                },
                entities: {
                    ...state.entities,
                    [newInvoice.id]: newInvoice,
                },
            };
        }

        case InvoiceActions.UPDATE_INVOICE_STATUS_SUCCESS: {
            const updatedInvoice = action.payload;
            return {
                ...state,
                isLoading: false,
                error: undefined,
                entities: {
                    ...state.entities,
                    [updatedInvoice.id]: updatedInvoice,
                },
            };
        }

        // Update the invoice status and email notification
        case InvoiceActions.SEND_TO_CLIENT_SUCCESS: {
            const { invoiceId, payload } = action;
            const existing = state.entities[invoiceId];

            if (!existing) return { ...state, isLoading: false, error: undefined };

            return {
                ...state,
                isLoading: false,
                error: undefined,
                entities: {
                    ...state.entities,
                    [invoiceId]: {
                        ...existing,
                        status: payload.invoiceStatus,
                        sendToCustomerEmailStatus: payload.sendToCustomerEmailStatus,
                    },
                },
            };
        }

        case InvoiceActions.GET_INVOICE_BY_ID_REQUEST:
        case InvoiceActions.DUPLICATE_INVOICE_REQUEST:
        case InvoiceActions.ARCHIVE_INVOICE_REQUEST:
        case InvoiceActions.UNARCHIVE_INVOICE_REQUEST:
        case InvoiceActions.UPDATE_INVOICE_REQUEST:
        case InvoiceActions.SEND_TO_CLIENT_REQUEST:
        case InvoiceActions.UPDATE_INVOICE_STATUS_REQUEST:
        case InvoiceActions.LIST_INVOICES_REQUEST: {
            return {
                ...state,
                isLoading: true,
                error: undefined,
            };
        }

        case InvoiceActions.GET_INVOICE_BY_ID_ERROR:
        case InvoiceActions.DUPLICATE_INVOICE_ERROR:
        case InvoiceActions.ARCHIVE_INVOICE_ERROR:
        case InvoiceActions.UNARCHIVE_INVOICE_ERROR:
        case InvoiceActions.UPDATE_INVOICE_ERROR:
        case InvoiceActions.SEND_TO_CLIENT_ERROR:
        case InvoiceActions.UPDATE_INVOICE_STATUS_ERROR:
        case InvoiceActions.LIST_INVOICES_ERROR: {
            return {
                ...state,
                isLoading: false,
                error: action.error,
            };
        }

        default: {
            return state;
        }
    }
};

export default invoicesReducer;
